export interface FeatureFlag {
  key: string;
  description: string;
}

export enum FeatureFlagKeys {
  UNAUTHENTICATED_ASSESSMENTS = 'UNAUTHENTICATED_ASSESSMENTS',
  RESOURCE_FEATURE = 'RESOURCE_FEATURE',
  PROGRESS_FEATURE = 'PROGRESS_FEATURE',
  ELIGIBILITY_API_SSO_CHANGES = 'ELIGIBILITY_API_SSO_CHANGES',
  ELIGIBILITY_API_NON_SSO_CHANGES = 'ELIGIBILITY_API_NON_SSO_CHANGES',
  SEARCH_CARE_PROGRAMS = 'SEARCH_CARE_PROGRAMS',
  BENEFITS_FEATURE = 'BENEFITS_FEATURE',
  PROFILE_FEATURE = 'PROFILE_FEATURE',
  GUIDING_CARE_ASSESSMENTS = 'GUIDING_CARE_ASSESSMENTS',
}

// Example of adding a feature flag
export const FEATURE_FLAGS: FeatureFlag[] = [
  {
    key: FeatureFlagKeys.ELIGIBILITY_API_SSO_CHANGES,
    description: 'Enable eligibility API endpoint changes for SSO',
  },
  {
    key: FeatureFlagKeys.ELIGIBILITY_API_NON_SSO_CHANGES,
    description: 'Enable eligibility API endpoint changes for Non-SSO',
  },
  {
    key: FeatureFlagKeys.PROGRESS_FEATURE,
    description: 'Enable progress feature',
  },
  {
    key: FeatureFlagKeys.RESOURCE_FEATURE,
    description: 'Enable resource features alongwith cobrands',
  },
  {
    key: FeatureFlagKeys.SEARCH_CARE_PROGRAMS,
    description: 'Enable care programs search',
  },
  {
    key: FeatureFlagKeys.UNAUTHENTICATED_ASSESSMENTS,
    description: 'Allow users to access unauthenticated assessments',
  },
  {
    key: FeatureFlagKeys.BENEFITS_FEATURE,
    description: 'Enable benefits feature',
  },
  {
    key: FeatureFlagKeys.PROFILE_FEATURE,
    description: 'Enable profile tab and page',
  },
  {
    key: FeatureFlagKeys.GUIDING_CARE_ASSESSMENTS,
    description: 'Enable guiding care assessments in member-web',
  },
];

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[number]['key'];

export const FEATURE_FLAGS_STORAGE_KEY = 'feature_flags';

/**
 * Parse a comma-separated string into a normalized array of cobrands
 * @param raw - Raw string or array to parse
 * @returns Normalized array of lowercase cobrand names
 */
const parseList = (raw?: string | string[]): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((s) => s.trim().toLowerCase()).filter(Boolean);
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
};

/**
 * Get the default enabled state for a feature flag from environment variables
 * Environment variable name is prefixed with REACT_APP_ per Create React App convention
 *
 * Supports three formats:
 * - Boolean: REACT_APP_<KEY>=true or REACT_APP_<KEY>=false (global enable/disable)
 * - Comma-separated cobrands: REACT_APP_<KEY>=bcbskc,bcbssc (enable for specific cobrands)
 * - Empty/undefined: Feature is disabled
 *
 * @param key - The feature flag key (e.g., 'RESOURCE_COBRAND')
 * @param cobrand - Optional cobrand name to check against (e.g., 'bcbskc')
 * @returns The default enabled state from env var, or undefined if not set
 */
export const getEnvDefaultForFlag = (key: string, cobrand?: string): boolean | undefined => {
  // Convert feature flag key to env var name: RESOURCE_COBRAND -> REACT_APP_RESOURCE_COBRAND
  const envVarName = `REACT_APP_${key}`;
  const envValue = process.env[envVarName];

  if (envValue === undefined) {
    return undefined;
  }

  const trimmed = envValue.trim().toLowerCase();

  // Handle boolean values
  if (trimmed === 'true') {
    return true;
  }
  if (trimmed === 'false') {
    return false;
  }

  // Handle comma-separated cobrand list
  const allowedCobrands = parseList(envValue);

  // If no cobrand provided, return false (cobrand-specific flags require explicit cobrand check)
  if (!cobrand) {
    return false;
  }

  // Check if the provided cobrand is in the allowed list
  const result = allowedCobrands.includes(cobrand.toLowerCase());
  return result;
};
